<script>
import axios from 'axios';
import { cmsUrl } from '../variables';
import { escapeHtml } from '../helpers';

import ContentTextBlock from '../components/ContentTextBlock';
import ContentFullIWidthImageBlock from '../components/ContentFullIWidthImageBlock';
import ContentQuoteBlock from '../components/ContentQuoteBlock';
import ContentReferencesBlock from '../components/ContentReferencesBlock';
import ContentContributorsBlock from '../components/ContentContributorsBlock';
import ContentOfferingsBlock from '../components/ContentOfferingsBlock';
import ContentEventsBlock from '../components/ContentEventsBlock';
import ContentProjectsBlock from '../components/ContentProjectsBlock';
import ContentLogoWallBlock from '../components/ContentLogoWallBlock';
import LoadIndicator from '../components/LoadIndicator';
import ContentEmergencyButtonBlock from '../components/ContentEmergencyButtonBlock';
import ContentConsultingFormBlock from '../components/ContentConsultingFormBlock';
import ContentHeroListBlock from '../components/ContentHeroListBlock';
import ContentArticleOverviewBlock from '../components/ContentArticleOverviewBlock';

export default {
    name: 'ContentPage',
    components: {
        ContentTextBlock,
        ContentFullIWidthImageBlock,
        ContentQuoteBlock,
        ContentReferencesBlock,
        ContentContributorsBlock,
        ContentOfferingsBlock,
        ContentEventsBlock,
        ContentProjectsBlock,
        ContentLogoWallBlock,
        LoadIndicator,
        ContentEmergencyButtonBlock,
        ContentConsultingFormBlock,
        ContentHeroListBlock,
        ContentArticleOverviewBlock
    },
    data: () => {
        return {
            pageData: null,
            error: null,
            isLoading: true,
        }
    },
    metaInfo() {
        if (this.pageData) {
            let meta = [
                { vmid: 'og:url', property: 'og:url', content: this.$t('global.url') + window.location.pathname }
            ];
            if (this.pageData.description) {
                meta.push({ vmid: 'description', name: 'description', content: this.pageData.description })
                meta.push({ vmid: 'og:description', property: 'og:description', content: this.pageData.description })
            }
            if (this.pageData.title)
                meta.push({ vmid: 'og:title', property: 'og:title', content: escapeHtml(this.pageData.title.rendered) });
            if (this.pageData.preview_img)
                meta.push({ vmid: 'og:image', property: 'og:image', content: this.pageData.preview_img });
            
            return {
                titleTemplate: this.pageData.seo_title ? this.pageData.seo_title : escapeHtml(this.pageData.title.rendered),
                link: [
                    { vmid: 'canonical', rel: 'canonical', href: this.$t('global.url') + window.location.pathname }
                ],
                meta: meta,
            }
        }
    },
    mounted() {
        this.$store.dispatch('setSiteHeaderOpaque');
        this.$store.dispatch('showSiteFooter');
        this.init(this.$route.params.pageName);
    },
    methods: {
        init(pageName) {
            axios
                .get(cmsUrl + '/pages?slug=' + pageName)
                .then(this.onDataLoaded)
                .catch(() => this.onError())
        },
        onDataLoaded(data) {
            if (!data || !data.data || data.data.length === 0) {
                this.onError();
                return;
            }
            this.pageData = data.data.length > 0 ? data.data[0] : null;
            this.isLoading = false;
        },
        onError() {
            this.$router.push('page-not-found');
            this.isLoading = false;
        },
        getComponentName(layout) {
            switch (layout) {
                case 'text_block':
                    return 'ContentTextBlock';

                case 'full_width_image':
                    return 'ContentFullIWidthImageBlock';

                case 'quote':
                    return 'ContentQuoteBlock';

                case 'references':
                    return 'ContentReferencesBlock';

                case 'contributors_overview':
                    return 'ContentContributorsBlock';

                case 'offerings_overview':
                    return 'ContentOfferingsBlock';

                case 'events_overview':
                    return 'ContentEventsBlock';

                case 'projects_overview':
                    return 'ContentProjectsBlock';

                case 'logo_wall':
                    return 'ContentLogoWallBlock';

                case 'emergency_button':
                    return 'ContentEmergencyButtonBlock';

                case 'consulting_form':
                    return 'ContentConsultingFormBlock';

                case 'hero_list':
                    return 'ContentHeroListBlock';

                case 'article_overview':
                    return 'ContentArticleOverviewBlock';

            }
        }
    },
    beforeRouteUpdate(to, from, next) {
        this.init(to.params.pageName);
        next();
    },
};
</script>

<template>
    <section class="content-page" v-bind:class="['content-page--' + $route.params.pageName]">
        <div class="content-page__wrapper">
            <div v-if="!isLoading && pageData">
                <div class="content-page__title-icon" v-bind:class="`content-page__title-icon--${pageData.title_icon}`" v-if="pageData.title_icon"></div>
                <h1 class="content-page__title page-title" v-html="pageData.title.rendered"></h1>
                <p class="content-page__lead page-lead">{{ pageData.page_lead }}</p>
                <component 
                    v-for="(block, i) in pageData.content_blocks" 
                    v-bind:key="i"
                    v-bind:data="block"
                    v-bind:is="getComponentName(block.acf_fc_layout)">
                </component>
            </div>
            <load-indicator v-if="isLoading" />
        </div>
    </section>
</template>

<style lang="scss" scoped>
.content-page {
    @include page;
}

.content-page__wrapper {
    @include container-narrow;
}

.content-page__title-icon--consulting,
.content-page__title-icon--triological {
    width: 13.6rem;
    height: 12.9rem;
    margin: 0 0 1.4rem -3.2rem;
    background-size: 100%;
    background-repeat: no-repeat;

    @include breakpoint('md-down') {
        margin-left: 0;
    }
}

.content-page__title-icon--consulting {
    background-image: url('~@/assets/images/logo-consulting.svg');
}

.content-page__title-icon--triological {
    background-image: url('~@/assets/images/logo-triological.svg');
}

// spacing rules
.hero-list.hero-list--icon + .text-block {
    margin-top: 8rem;
}

</style>
